import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import useFetch from "../lib/useFetch";

const API = process.env.REACT_APP_API;

async function api<T = unknown>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const res = await fetch(`${API}/api/semeadora${path}`, options);
  if (!res.ok) throw new Error(await res.text());
  // tenta JSON, senão texto
  const text = await res.text();
  return text ? (JSON.parse(text) as T) : ({} as T);
}

export type Transaction = {
  id: number;
  bank_date: string;
  amount: string;
  description: string;
  content_type: "order" | "farmerorder" | null;
  object_id: number | null;
  note: string;
  category: string | null;
  fair_id: number | null;
  seed: string;
  label: string;
};

export interface TransactionPage {
  count: number;
  next: string | null;
  previous: string | null;
  results: Transaction[];
}

export type Category = { id: number; slug: string; name: string };

export function useUploadCSV() {
  const fetch = useFetch();
  return useMutation<{ batch_id: number }, Error, File>((file) => {
    const form = new FormData();
    form.append("file", file);
    return fetch(`${API}/api/semeadora/upload-csv/`, {
      method: "POST",
      body: form,
      formData: true,
    });
  });
}

export function useTransactions({
  start,
  end,
  matched,
  page,
}: {
  start?: string;
  end?: string;
  matched?: string;
  page: number;
}) {
  const fetch = useFetch();
  const filters: Record<string, string> = {
    page: page.toString(),
  };

  if (start) {
    filters["start"] = start;
  }

  if (end) {
    filters["end"] = end;
  }

  if (matched) {
    filters["matched"] = matched;
  }

  const query = new URLSearchParams(filters).toString();

  return useQuery<TransactionPage, Error>(["transactions", query], () =>
    fetch(`${API}/api/semeadora/transactions/?${query}`),
  );
}

export function useUpdateTransaction() {
  const qc = useQueryClient();
  const fetch = useFetch();
  return useMutation<
    Transaction,
    Error,
    { id: number; data: Partial<Transaction> }
  >(
    ({ id, data }) =>
      fetch(`${API}/api/semeadora/transactions/${id}/`, {
        method: "PATCH",
        body: data,
      }),
    {
      onSuccess: (_, { id }) => {
        // atualiza cache
        qc.invalidateQueries({ queryKey: ["transactions"] });
      },
    },
  );
}

export function useCategories() {
  return useQuery<Category[], Error>(["categories"], () =>
    api<Category[]>("/transactions/categories/"),
  );
}
